// ---------------------------------------------------------------------------
// ClaudeClient — Wraps @anthropic-ai/sdk for browser-side requirement
// extraction via Claude's vision + text APIs.
// ---------------------------------------------------------------------------

import Anthropic from '@anthropic-ai/sdk';
import type { ExtractedRequirement, ExtractionResult, ClaudeApiUsage } from './ingestion-types.js';

/** Media types accepted by Claude's vision API */
type ImageMediaType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';

/** Claude model to use for extraction */
const MODEL = 'claude-sonnet-4-20250514';

/** Max retries on transient failures */
const MAX_RETRIES = 3;

/** Vision extraction prompt — exported for audit trail transparency */
export const VISION_EXTRACTION_PROMPT = `Extract ALL text from this image exactly as it appears. Preserve the structure (tables, lists, headings, numbering). If this is a requirements table, maintain the row/column structure. Output ONLY the extracted text, no commentary.`;

/** Requirements extraction system prompt — exported for audit trail transparency */
export const REQUIREMENTS_EXTRACTION_SYSTEM_PROMPT = `You are a systems engineering expert specializing in SysML v2 requirements analysis. Extract structured requirements from the provided text.

The source document may be in ANY format — formal spec, email thread, meeting notes, annotated worksheet, standards excerpt, spreadsheet, etc. Regardless of format, identify every discrete requirement statement and extract it.

For each requirement, produce:
- name: A SysML-safe identifier. If the source uses an existing ID scheme (e.g., SAF-005, PER-007, REQ-42), preserve it as the name (converted to SysML-safe: SAF_005, PER_007, REQ_042). Otherwise generate a descriptive PascalCase name (e.g., REQ_SafetyBraking_001). Must start with a letter.
- title: A concise human-readable title
- text: The full requirement statement. Use "shall" for mandatory requirements.
- priority: One of "critical", "high", "medium", "low" — infer from context if not stated
- rationale: Why this requirement exists (if stated or inferable)
- source: The document/section/standard this came from (e.g., "UL 1026 §28", "Meeting 2/12 — Martinez")
- confidence: Your confidence that this is a genuine requirement (0.0-1.0). Lower confidence for items that are discussion points vs. firm requirements.
- parentId: If this requirement derives from or is a sub-requirement of another, the name of the parent. This includes requirements that explicitly reference a parent (e.g., "derives from PER-004") AND requirements from the existing model context if provided.
- depth: Nesting level (0 for top-level, 1 for sub-requirements, etc.)
- verificationMethod: How this requirement should be verified — one of "test", "analysis", "inspection", "demonstration" (if stated or inferable)
- existingMatch: If this requirement appears to duplicate or overlap with an existing model element, state the name of the existing element. Otherwise omit.

Respond with ONLY a JSON object in this exact format:
{
  "requirements": [...],
  "warnings": ["any extraction warnings or notes about ambiguous items"]
}`;

/** SysML generation system prompt — exported for audit trail transparency */
export const SYSML_GENERATION_SYSTEM_PROMPT = `You are a SysML v2 expert. Generate valid SysML v2 textual notation for the given requirements.

Rules:
- Use \`requirement def\` for top-level requirement definitions
- Use nested \`requirement\` (usage) for sub-requirements inside a parent def
- Include \`doc /* ... */\` for the requirement text
- Use comments for rationale
- If priority or source metadata exists, include as \`@meta { ... }\`
- If a verificationMethod is provided, generate a corresponding \`verification def\` with the appropriate method attribute
- If a requirement has a parentId that matches an existing definition in the model, use \`requirement <name> :> <parentName>\` to establish the derivation
- If existing SysML source is provided, generate ONLY the new definitions that should be appended — do NOT repeat or modify existing definitions
- Place new requirements inside the existing package structure if one exists
- Output ONLY the SysML v2 code, no explanation or markdown fences`;

export class ClaudeClient {
  private client: Anthropic | null = null;

  /** Initialize with an API key. Call before using any methods. */
  configure(apiKey: string): void {
    this.client = new Anthropic({
      apiKey,
      dangerouslyAllowBrowser: true,
    });
  }

  get isConfigured(): boolean {
    return this.client !== null;
  }

  // ════════════════════════════════════════════════════════════════════════
  // Vision: Extract raw text from an image
  // ════════════════════════════════════════════════════════════════════════

  async extractTextFromImage(
    base64Data: string,
    mediaType: ImageMediaType,
  ): Promise<{ data: string; usage: ClaudeApiUsage }> {
    this._assertConfigured();

    const startTime = Date.now();
    const response = await this.client!.messages.create({
      model: MODEL,
      max_tokens: 4096,
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'image',
              source: {
                type: 'base64',
                media_type: mediaType,
                data: base64Data,
              },
            },
            {
              type: 'text',
              text: VISION_EXTRACTION_PROMPT,
            },
          ],
        },
      ],
    });

    return {
      data: this._extractTextContent(response),
      usage: this._extractUsage(response, Date.now() - startTime),
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // Extract structured requirements from raw text
  // ════════════════════════════════════════════════════════════════════════

  async extractRequirements(
    rawText: string,
    context?: string,
  ): Promise<ExtractionResult> {
    this._assertConfigured();
    const startTime = Date.now();

    const systemPrompt = REQUIREMENTS_EXTRACTION_SYSTEM_PROMPT;

    const userMessage = context
      ? `Context about the project:\n${context}\n\n---\n\nExtract requirements from the following text:\n\n${rawText}`
      : `Extract requirements from the following text:\n\n${rawText}`;

    const response = await this.client!.messages.create({
      model: MODEL,
      max_tokens: 4096,
      system: systemPrompt,
      messages: [{ role: 'user', content: userMessage }],
    });

    const text = this._extractTextContent(response);
    const parsed = this._parseJsonResponse(text);
    const durationMs = Date.now() - startTime;

    const rawReqs = Array.isArray(parsed.requirements) ? parsed.requirements : [];
    const requirements: ExtractedRequirement[] = rawReqs.map(
      (r: Record<string, unknown>, index: number) => ({
        id: crypto.randomUUID(),
        name: String(r.name ?? `REQ_${String(index + 1).padStart(3, '0')}`),
        title: String(r.title ?? ''),
        text: String(r.text ?? ''),
        priority: this._normalizePriority(r.priority),
        rationale: r.rationale ? String(r.rationale) : undefined,
        source: r.source ? String(r.source) : undefined,
        confidence: typeof r.confidence === 'number' ? r.confidence : 0.8,
        parentId: r.parentId ? String(r.parentId) : undefined,
        depth: typeof r.depth === 'number' ? r.depth : 0,
        verificationMethod: this._normalizeVerificationMethod(r.verificationMethod),
        existingMatch: r.existingMatch ? String(r.existingMatch) : undefined,
        duplicates: undefined,
        accepted: true,
      }),
    );

    return {
      rawText,
      requirements,
      durationMs,
      warnings: Array.isArray(parsed.warnings)
        ? parsed.warnings.map(String)
        : [],
      apiUsage: this._extractUsage(response, durationMs),
      rawResponseText: text,
      systemPromptSent: systemPrompt,
      contextStringSent: context,
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // Generate SysML v2 source from extracted requirements (fallback for
  // complex hierarchies where the local builder schema is insufficient)
  // ════════════════════════════════════════════════════════════════════════

  async generateSysmlFromRequirements(
    requirements: ExtractedRequirement[],
    existingSource?: string,
  ): Promise<{ data: string; usage: ClaudeApiUsage; systemPrompt: string; userPrompt: string }> {
    this._assertConfigured();

    const systemPrompt = SYSML_GENERATION_SYSTEM_PROMPT;

    const reqList = requirements
      .map((r) => `- ${r.name}: "${r.title}" — ${r.text} (priority: ${r.priority}, depth: ${r.depth}${r.parentId ? `, parent: ${r.parentId}` : ''})`)
      .join('\n');

    const userMessage = existingSource
      ? `Here is the existing SysML source:\n\`\`\`\n${existingSource}\n\`\`\`\n\nGenerate requirement definitions for these extracted requirements (avoid duplicating existing definitions):\n${reqList}`
      : `Generate SysML v2 requirement definitions for:\n${reqList}`;

    const startTime = Date.now();
    const response = await this.client!.messages.create({
      model: MODEL,
      max_tokens: 4096,
      system: systemPrompt,
      messages: [{ role: 'user', content: userMessage }],
    });

    return {
      data: this._extractTextContent(response),
      usage: this._extractUsage(response, Date.now() - startTime),
      systemPrompt,
      userPrompt: userMessage,
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal helpers
  // ════════════════════════════════════════════════════════════════════════

  private _assertConfigured(): void {
    if (!this.client) {
      throw new Error('ClaudeClient not configured. Call configure(apiKey) first.');
    }
  }

  private _extractUsage(response: Anthropic.Message, durationMs: number): ClaudeApiUsage {
    const usage = response.usage;
    return {
      model: response.model,
      inputTokens: usage?.input_tokens ?? 0,
      outputTokens: usage?.output_tokens ?? 0,
      cacheTokens: (usage as unknown as Record<string, number>)?.cache_read_input_tokens ?? 0,
      durationMs,
      stopReason: response.stop_reason ?? 'unknown',
    };
  }

  private _extractTextContent(response: Anthropic.Message): string {
    for (const block of response.content) {
      if (block.type === 'text') return block.text;
    }
    return '';
  }

  private _parseJsonResponse(text: string): Record<string, unknown> {
    // Strip markdown code fences if present
    let cleaned = text.trim();
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
    }
    try {
      return JSON.parse(cleaned) as Record<string, unknown>;
    } catch {
      // Try to find JSON object in the response
      const match = cleaned.match(/\{[\s\S]*\}/);
      if (match) {
        return JSON.parse(match[0]) as Record<string, unknown>;
      }
      throw new Error('Failed to parse AI response as JSON');
    }
  }

  private _normalizePriority(
    value: unknown,
  ): 'critical' | 'high' | 'medium' | 'low' {
    const s = String(value ?? 'medium').toLowerCase();
    if (s === 'critical') return 'critical';
    if (s === 'high') return 'high';
    if (s === 'low') return 'low';
    return 'medium';
  }

  private _normalizeVerificationMethod(
    value: unknown,
  ): 'test' | 'analysis' | 'inspection' | 'demonstration' | undefined {
    if (!value) return undefined;
    const s = String(value).toLowerCase();
    if (s === 'test') return 'test';
    if (s === 'analysis') return 'analysis';
    if (s === 'inspection' || s === 'inspect') return 'inspection';
    if (s === 'demonstration' || s === 'demo') return 'demonstration';
    return undefined;
  }
}
